import { Injectable, Type } from '@angular/core';

import { PointSettingTypeEnum } from '@shared/enum/member-point.enum';

import { environment } from '../../../environments/environment';
import { GiftWithPurchaseModeEnum } from '../../pages/gift-with-purchase/enum/gift-with-purchase-request.enum';
import { PimModeEnum } from '../../pages/pim/enum/pim.enum';
import { PromotionStatusEnum } from '../../pages/promotion-v2/enum/promotion-v2.enum';
import { ReceiptCouponModeEnum } from '../../pages/receipt-coupon/enums/receipt-coupon-request.enum';
import { StoreAvailableAdjustmentRequestViewComponent } from '../../pages/store-available-adjustment/component/store-available-adjustment-request-view/store-available-adjustment-request-view.component';
import { ManageStockPageModes } from '../../pages/store-stock-planning/enum/ssr-result.enum';
import { ChildItem } from '../layouts/modals/full-modal/child-item';
import { DocType, PortalModule } from '../models';

@Injectable()
export class ModuleUtil {
  /**
   * @description Initial state for FullModalComponent
   */
  public static InitialState(
    item: any,
    component: Type<any>,
    mode: string,
    title: string,
    originPage: string,
    portalModule: string = null
  ) {
    if (portalModule == null || portalModule.length === 0) {
      portalModule = item.module;
    }

    const objValue = {
      title,
      mode,
      originPage
    };

    switch (portalModule) {
      case PortalModule.PURCHASE_REQUEST:
        return {
          title: null,
          childItem: new ChildItem(
            component,
            {
              ...objValue,
              prNo: item.id,
              status: item.status,
              type: item.type
            },
            true
          )
        };
      case PortalModule.PROMOTION:
        return {
          title: null,
          childItem: new ChildItem(
            component,
            {
              ...objValue,
              ...(environment.prefixCompany === 'CJX'
                ? { id: item.id, status: PromotionStatusEnum.AWAITING_APPROVAL }
                : { promotionRequestId: item.id })
            },
            true
          )
        };
      case PortalModule.CLAIM:
        return {
          title: null,
          childItem: new ChildItem(
            component,
            {
              ...objValue,
              claimRequestNo: item.claimRequestNo,
              receiveOrderNo: item.receiveOrderNo,
              claimRequestStatus: item.claimRequestStatus
            },
            true
          )
        };
      case PortalModule.VOUCHER:
      case PortalModule.REWARD:
      case PortalModule.SHELF_REQUEST:
      case PortalModule.SHELF_FIX_ASSET_REQUEST:
      case PortalModule.SHELF_INVENTORY_REQUEST:
        return {
          title: null,
          childItem: new ChildItem(
            component,
            {
              ...objValue,
              requestId: item.id,
              requestNo: item.requestNo
            },
            true
          )
        };
      case PortalModule.REWARD_CATALOG:
        return {
          title: null,
          childItem: new ChildItem(
            component,
            {
              ...objValue,
              rewardCatalogRequestId: item.id
            },
            true
          )
        };
      case PortalModule.MERCHANT:
        return {
          title: null,
          childItem: new ChildItem(
            component,
            {
              ...objValue,
              merchantRequestId: item.id,
              type: item.type
            },
            true
          )
        };
      case PortalModule.STORE:
        return {
          title: null,
          childItem: new ChildItem(
            component,
            {
              ...objValue,
              storeId: item.id,
              merchant: item.merchantNo
            },
            true
          )
        };
      case PortalModule.TASK_REQUEST:
        return {
          title: null,
          childItem: new ChildItem(
            component,
            {
              ...objValue,
              id: item.id
            },
            true
          )
        };
      case PortalModule.TASK_ASSIGNMENT_RESPONSE:
        return {
          title: null,
          childItem: new ChildItem(
            component,
            {
              ...objValue,
              id: item.id,
              responseNo: item.requestNo
            },
            true
          )
        };
      case PortalModule.ORDER:
        return {
          title: null,
          childItem: new ChildItem(
            component,
            {
              ...objValue,
              requestId: item.id,
              orderType: item.orderType,
              orderSubType: item.orderSubType,
              orderFlow: item.orderFlow,
              crossDockSupplier: item.crossDockSupplier
            },
            true
          )
        };
      case PortalModule.PRODUCT:
        return {
          title: null,
          childItem: new ChildItem(
            component,
            {
              ...objValue,
              id: item.id,
              articleNo: item.requestInfo.articleNo,
              articleType: item.requestInfo.articleType,
              productType: item.requestInfo.productType,
              requestNo: item.requestNo
            },
            true
          )
        };
      case PortalModule.STOCK_TRANSFER:
        return {
          title: null,
          childItem: new ChildItem(
            component,
            {
              ...objValue,
              id: item.id,
              shipFrom: item.shipFromCodeName,
              shipTo: item.shipToCodeName,
              status: item.requestStatus
            },
            true
          )
        };
      case PortalModule.PURCHASE_CONDITION:
        return {
          title: null,
          childItem: new ChildItem(
            component,
            {
              ...objValue,
              id: item.id,
              supplierCode: item.supplierCode,
              supplierName: item.supplierName
            },
            true
          )
        };
      case PortalModule.POINT_EARN_SETTING:
        if (item.type === PointSettingTypeEnum.SPECIAL_POINT) {
          return {
            title: null,
            childItem: new ChildItem(
              component,
              {
                ...objValue,
                type: item.type,
                id: item.id
              },
              true
            )
          };
        } else {
          return {
            title: null,
            childItem: new ChildItem(
              component,
              {
                ...objValue,
                type: item.type,
                requestNo: item.requestNo
              },
              true
            )
          };
        }
      case PortalModule.POINT_BURN_SETTING:
        return {
          title: null,
          childItem: new ChildItem(
            component,
            {
              ...objValue,
              type: item.type,
              requestNo: item.requestNo
            },
            true
          )
        };

      case PortalModule.REFUND:
        return {
          title: null,
          childItem: new ChildItem(
            component,
            {
              ...objValue,
              id: item.id,
              type: item.type,
              requestNo: item.requestNo
            },
            true
          )
        };
      case PortalModule.HOLD_ASSORTMENT:
        return {
          title: null,
          childItem: new ChildItem(
            component,
            {
              ...objValue,
              requestNo: item.requestNo
            },
            true
          )
        };
      case PortalModule.COUPON_SETTING_REQUEST:
        return {
          title: null,
          childItem: new ChildItem(
            component,
            {
              ...objValue,
              id: item.id
            },
            true
          )
        };
      case PortalModule.TOTE:
        return {
          title: null,
          childItem: new ChildItem(
            component,
            {
              ...objValue,
              toteTicketRequestNo: item.requestNo
            },
            true
          )
        };
      case PortalModule.STORE_ASSORTMENT:
        return {
          title: null,
          childItem: new ChildItem(
            component,
            {
              ...objValue,
              requestNo: item.requestNo
            },
            true
          )
        };
      case PortalModule.RETURN_TO_WAREHOUSE:
        return {
          title: null,
          childItem: new ChildItem(
            component,
            {
              ...objValue,
              requestNo: item.requestNo
            },
            true
          )
        };
      case PortalModule.DESTROY_REQUEST:
        return {
          title: null,
          childItem: new ChildItem(
            component,
            {
              title: 'View Destroy Request',
              requestNo: item.requestNo
            },
            true
          )
        };
      case PortalModule.REDUCE_TO_CLEAR_REQUEST:
        return {
          title: null,
          childItem: new ChildItem(
            component,
            {
              title: 'View Reduce to Clear Request',
              requestNo: item.requestNo
            },
            true
          )
        };
      case PortalModule.SPECIAL_RETAIL_PRICE_REQUEST:
        return {
          title: null,
          childItem: new ChildItem(
            component,
            {
              requestNo: item.id
            },
            true
          )
        };
      case PortalModule.STOCK_COUNT:
        return {
          title: null,
          childItem: new ChildItem(
            StoreAvailableAdjustmentRequestViewComponent,
            {
              title: 'View Store Available Adjustment Request',
              docNo: item.requestNo
            },
            false
          )
        };
      case PortalModule.RECEIPT_COUPON:
        return {
          title: null,
          childItem: new ChildItem(
            component,
            {
              title: 'View Receipt Coupon Request',
              mode: ReceiptCouponModeEnum.REQUEST_VIEW,
              id: item.id,
              receiptCouponType: item.receiptCouponType
            },
            true
          )
        };
      case PortalModule.STORE_EXPENSE:
        return {
          title: null,
          childItem: new ChildItem(
            component,
            {
              title: 'Approve Store Expense Request',
              docNo: item.requestNo
            },
            true
          )
        };
      case PortalModule.GIFT_WITH_PURCHASE:
        return {
          title: null,
          childItem: new ChildItem(
            component,
            {
              title: 'Approve Gift with Purchase Request',
              mode: GiftWithPurchaseModeEnum.REQUEST_VIEW,
              id: item.requestNo
            },
            true
          )
        };
      case PortalModule.STOCK_ADJUSTMENT:
        return {
          title: null,
          childItem: new ChildItem(
            component,
            {
              title: 'Approve Stock Adjustment Request',
              docNo: item.requestNo,
              mode: ManageStockPageModes.ADJUST_STOCK_VIEW,
              requestStatus: item.requestStatus
            },
            true
          )
        };
      case PortalModule.PRODUCT_INFORMATION_MANAGEMENT:
        switch (item.docType) {
          case DocType.BULK_IMPORT_REQUEST:
            return {
              title: null,
              childItem: new ChildItem(
                component,
                {
                  ...objValue,
                  mode: 'VIEW_REQUEST',
                  requestId: item.id,
                  cssInner: 'p-0'
                },
                false
              )
            };
          default:
            return {
              title: null,
              childItem: new ChildItem(
                component,
                {
                  ...objValue,
                  title: 'View Product Request',
                  requestId: item.id,
                  mode: PimModeEnum.VIEW_REQUEST,
                  requestStatus: item.requestStatus
                },
                true
              )
            };
        }
      default:
        return null;
    }
  }

  /**
   * @description Initial state for Router
   */
  public static InitialRouter(navigate: string, state: any) {
    return {
      navigate: navigate,
      state: state
    };
  }
}
